import React from "react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO.jsx"
import RelatedServices from "../components/page/RelatedServices.jsx"
import { CHATBOT_PATH, CONTACT_PROJECT, CONTACT_QUOTE } from "../data/services.js"
import { FEATURED_PROJECTS, SCREENSHOT_SIZE } from "../data/portfolio.js"

// Contact routes with website development preselected.
const WEBSITE_ENQUIRY = `${CONTACT_PROJECT}&service=website-development`
const WEBSITE_QUOTE = `${CONTACT_QUOTE}&service=website-development`

const PRIMARY_BUTTON = "inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-7 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
const SECONDARY_BUTTON = "inline-flex min-h-[48px] items-center justify-center rounded-full border border-[rgba(22,53,90,0.14)] bg-brand-surface px-6 py-3 text-[0.95rem] font-bold text-[#16355a] transition hover:-translate-y-[1px] hover:border-brand-primary"
const LIGHT_BUTTON = "inline-flex min-h-[52px] items-center justify-center rounded-full bg-white px-7 py-3 text-[0.98rem] font-bold text-brand-navy shadow-[0_16px_30px_rgba(0,0,0,0.18)] transition hover:-translate-y-[1px]"

function ProjectScreenshot({ src, alt, mobile = false }) {
  const [loaded, setLoaded] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const size = mobile ? SCREENSHOT_SIZE.mobile : SCREENSHOT_SIZE.desktop

  return (
    <div className={`relative overflow-hidden bg-[#eaf0f7] ${mobile ? 'aspect-[9/19]' : 'aspect-[16/10]'}`}>
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs font-medium text-[#718399]">
          Preview unavailable
        </div>
      )}
      {!failed && (
        <img
          src={src}
          alt={alt}
          width={size.width}
          height={size.height}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          // Long screenshots pan slowly down on hover to show more of the page.
          className={`absolute inset-0 h-full w-full object-cover object-top transition-[opacity,object-position] duration-[400ms,5000ms] ease-in-out group-hover:object-bottom motion-reduce:transition-none motion-reduce:group-hover:object-top ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  )
}

function domainOf(url) {
  return new URL(url).hostname.replace(/^www\./, '')
}

function SelectedWork() {
  return (
    <section id="selected-work" className="bg-white py-section" aria-labelledby="selected-work-title">
      <div className="mx-auto max-w-[1180px] px-6">
        <div className="mb-10 max-w-[800px]">
          <p className="mb-4 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">Selected work</p>
          <h2 id="selected-work-title" className="mb-4 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
            Real websites. Built around real businesses.
          </h2>
          <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
            Every business works differently. These projects show how custom design and development
            can support online sales, day-to-day operations and content management.
          </p>
        </div>

        <div className="grid gap-7 lg:grid-cols-3">
          {FEATURED_PROJECTS.map(project => (
            <article key={project.name} className="flex flex-col overflow-hidden rounded-xl2 border border-brand-border bg-brand-surface shadow-card">
              <div className="group relative bg-gradient-to-br from-[#eef3f9] to-[#dfe8f2] p-5 pb-0">
                <div className="overflow-hidden rounded-lg border border-brand-border bg-white shadow-sm">
                  <div className="flex h-7 items-center gap-1.5 border-b border-brand-border bg-white px-3" aria-hidden="true">
                    <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
                    <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
                    <span className="h-2 w-2 rounded-full bg-[#d4dce6]" />
                    <span className="ml-2 truncate rounded bg-[#f1f4f8] px-2 py-0.5 text-[0.65rem] text-[#718399]">{domainOf(project.url)}</span>
                  </div>
                  <ProjectScreenshot src={project.desktop} alt={project.desktopAlt} />
                </div>
                <div className="absolute bottom-[-14px] right-6 w-[24%] max-w-[150px] overflow-hidden rounded-[16px] border-[4px] border-[#193653] bg-white shadow-lg sm:w-[20%] lg:w-[27%]">
                  <ProjectScreenshot src={project.mobile} alt={project.mobileAlt} mobile />
                </div>
              </div>
              <div className="flex flex-1 flex-col p-6 pt-10">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.13em] text-brand-primary">{project.category}</p>
                <h3 className="mb-3 text-xl font-bold text-brand-heading">{project.name}</h3>
                <p className="mb-3 leading-[1.65] text-brand-textSoft">{project.summary}</p>
                <p className="mb-6 text-sm font-medium text-[#48617c]">{project.detail}</p>
                {project.url ? (
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex min-h-[44px] items-center font-semibold text-brand-primary hover:underline">
                    View live website <span aria-hidden="true" className="ml-2">↗</span>
                  </a>
                ) : (
                  <span className="mt-auto text-sm text-brand-textSoft">Live project link coming soon</span>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-6 overflow-hidden rounded-xl3 bg-gradient-to-br from-brand-navy to-[#16406f] p-8 text-white shadow-[0_25px_60px_rgba(10,35,67,0.18)] sm:p-10 md:flex-row md:items-center md:gap-10">
          <div className="hidden shrink-0 items-end md:flex" aria-hidden="true">
            {FEATURED_PROJECTS.map((project, index) => (
              <img
                key={project.name}
                src={project.mobile}
                alt=""
                width={SCREENSHOT_SIZE.mobile.width}
                height={SCREENSHOT_SIZE.mobile.height}
                loading="lazy"
                decoding="async"
                className={`h-[120px] w-[64px] rounded-[10px] border-[3px] border-white/90 object-cover object-top shadow-lg ${index ? '-ml-4' : ''} ${index === 1 ? 'relative z-10 -translate-y-2' : ''}`}
              />
            ))}
          </div>
          <h2 className="max-w-[560px] flex-1 text-[clamp(1.5rem,2.4vw,2.1rem)] font-bold leading-[1.2] text-white">
            Like what you see? Let's build something for your business.
          </h2>
          <Link to={WEBSITE_ENQUIRY} className={`${LIGHT_BUTTON} w-full shrink-0 sm:w-auto`}>
            Discuss Your Website
          </Link>
        </div>
      </div>
    </section>
  )
}

export default function WebDesignAdelaide() {
  return (
    <div className="bg-brand-bg text-brand-text">
      <SEO
        title="Web Design Adelaide | Business Website Design | 2merative"
        description="Web design Adelaide for established businesses. Premium custom websites that build credibility, support search visibility and turn visitors into quality enquiries."
        path="/web-design-adelaide"
        image="/og-image.png"
      />

      <section className="brand-hero pt-28 pb-section text-white">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_0.85fr]">
            <div className="max-w-[720px]">
              <div className="flex h-8 items-center">
                <p className="brand-hero-pill">Web Design Adelaide</p>
              </div>

              <h1 className="m-0 mt-14 text-[clamp(2.8rem,6vw,5rem)] font-bold leading-[0.96] tracking-[-0.03em] text-white">
                Premium websites built to
                <span className="block text-blue-100">
                  win better enquiries
                </span>
              </h1>

              <p className="mt-6 max-w-[650px] text-[1.12rem] leading-[1.85] text-blue-50">
                Your website should reflect the quality of your business, explain
                clearly what you do, and help the right customers take action. If
                your current site feels dated or no longer represents how you
                operate, it may be costing you trust before a conversation begins.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={WEBSITE_ENQUIRY}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-6 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
                >
                  Discuss Your Project
                </Link>

                <Link
                  to={WEBSITE_QUOTE}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-[rgba(22,53,90,0.14)] bg-brand-surface px-6 py-3 text-[0.98rem] font-bold text-[#16355a] transition hover:-translate-y-[1px]"
                >
                  Request a Quote
                </Link>
              </div>

              <ul className="mt-6 flex flex-wrap gap-3">
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Modern React builds
                </li>
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Conversion-focused structure
                </li>
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Clear local business positioning
                </li>
              </ul>
            </div>

            <aside className="rounded-xl3 border border-brand-border bg-white p-6 shadow-[0_25px_60px_rgba(17,36,58,0.08)] backdrop-blur-[10px]">
              <div>
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Built for
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Established businesses
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Focus
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Clarity, trust, enquiries
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Approach
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Lean, modern, no outdated code
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Best result
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Better flow from visit to contact
                </strong>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <SelectedWork />

      <section className="bg-brand-surface py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="mb-12 max-w-[760px]">
            <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
              The problem
            </p>
            <h2 className="mb-4 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
              Many businesses do not have a traffic problem. They have a trust
              and clarity problem.
            </h2>
            <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
              A website can be live for years and still underperform if it does
              not create confidence quickly. The layout, language, structure,
              and calls to action all shape whether someone stays, trusts you,
              and gets in touch.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Clear first impression
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                Your visitors should understand who you are, what you offer, and
                why they should trust you within seconds.
              </p>
            </article>

            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Better customer flow
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                Good web design guides people naturally from interest to
                enquiry, without confusion, clutter, or dead ends.
              </p>
            </article>

            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Stronger business presence
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                A polished website helps your business feel established,
                current, and professional across desktop and mobile.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#eef5ff] to-brand-surface py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="max-w-[760px]">
            <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
              What businesses usually need
            </p>
            <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
              Most businesses in Adelaide do not need a complicated website.
              They need a clear one.
            </h2>

            <div className="space-y-5 text-brand-textSoft">
              <p className="leading-[1.75]">
                A well-designed website should be easy to navigate, work smoothly on
                mobile and make it obvious what customers can do next. The design
                should serve your business rather than distract from it.
              </p>
              <p className="leading-[1.75]">
                In many cases, the problem is not that a business has no
                website. It is that the existing one no longer reflects the
                quality of the business behind it. The layout feels old. The
                messaging is unclear. The structure does not guide people
                anywhere.
              </p>
              <p className="leading-[1.75]">
                That is where thoughtful web design makes a difference. The goal
                is not to add noise. It is to create a website that feels well
                considered, easy to use, and aligned with the business it
                represents.
              </p>
            </div>

            <div className="mt-10 flex flex-col gap-5 border-t border-[rgba(22,53,90,0.12)] pt-8 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-[1.2rem] font-semibold leading-[1.4] text-brand-heading">
                Your website should be working harder for your business.
              </h3>
              <Link to={WEBSITE_ENQUIRY} className={`${SECONDARY_BUTTON} w-full shrink-0 sm:w-auto`}>
                Let's Talk About Yours
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-start gap-10 md:grid-cols-2">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
                What 2merative focuses on
              </p>
              <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
                Clear structure, strong messaging, and a better path to enquiry
              </h2>

              <div className="space-y-4 text-brand-textSoft">
                <p className="leading-[1.75]">
                  2merative builds websites with clarity first. That means
                  strong structure, clean visual hierarchy, practical content
                  flow, and a design that supports real business use.
                </p>
                <p className="leading-[1.75]">
                  The aim is not to overwhelm you with jargon or overbuilt
                  features. The aim is to create a website that feels right for
                  your business and helps you present properly online.
                </p>
                <p className="leading-[1.75]">
                  That might mean a full rebuild, a sharper service page
                  structure, stronger calls to action, or a better overall
                  customer journey. The right answer depends on where your
                  current website is helping and where it is getting in the way.
                </p>
                <p className="font-medium leading-[1.75] text-brand-heading">
                  We are based in Adelaide and love supporting local businesses.
                  If you are outside Adelaide, do not worry. We can still help
                  you too.
                </p>
              </div>
            </div>

            <div className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-5 text-2xl font-semibold text-brand-heading">
                This may be a good fit if
              </h3>

              <ul className="space-y-3 leading-[1.75] text-brand-textSoft">
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Your website looks dated compared with the quality of your
                  business.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You are getting traffic but not enough enquiries.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Your site feels unclear, weak, or disconnected from your
                  brand.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You want something more credible, modern, and easier to grow
                  with.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You want local Adelaide support and practical advice, not
                  technical noise.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-start gap-10 md:grid-cols-2">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
                Beyond the build
              </p>
              <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
                A new website can also connect to how your business runs
              </h2>

              <div className="space-y-4 text-brand-textSoft">
                <p className="leading-[1.75]">
                  Alongside the design and build, we can connect your website to the systems
                  your business already relies on. That might mean routing enquiries straight
                  into your CRM, syncing a booking system, linking Airtable or another
                  database, or connecting your email marketing platform.
                </p>
                <p className="leading-[1.75]">
                  The aim is simple: fewer manual steps, less double handling, and information
                  that reaches the right place without someone re-typing it.
                </p>
                <p className="leading-[1.75]">
                  What is possible depends on the platforms involved. We assess this as part of
                  scoping your project, and we are upfront about any technical or third-party
                  limitations before work begins.
                </p>
              </div>
            </div>

            <div className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-5 text-2xl font-semibold text-brand-heading">
                Practical examples
              </h3>

              <ul className="space-y-3 leading-[1.75] text-brand-textSoft">
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Enquiry forms that route straight into your CRM
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Booking systems connected to your website and calendar
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Airtable or database connections for services, pricing or content
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Email marketing platforms linked to sign-ups and enquiries
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Automated handoff from a form to the system your team checks
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  An{" "}
                  <Link to={CHATBOT_PATH} className="font-semibold text-brand-primary hover:underline">
                    AI chatbot
                  </Link>{" "}
                  that answers common questions and guides visitors towards an enquiry
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-5 rounded-xl2 border border-[rgba(29,95,208,0.14)] bg-[#eef5ff] p-7 sm:p-8 md:flex-row md:items-center md:justify-between">
            <div className="max-w-[640px]">
              <h3 className="mb-2 text-[1.25rem] font-semibold leading-[1.4] text-brand-heading">
                Have something specific in mind? Let's explore what's possible.
              </h3>
              <p className="leading-[1.65] text-brand-textSoft">
                From online bookings and e-commerce to customer management and business systems, we can
                build a website that works with the way your business operates.
              </p>
            </div>
            <Link to={WEBSITE_ENQUIRY} className={`${PRIMARY_BUTTON} w-full shrink-0 sm:w-auto`}>
              Discuss Your Project
            </Link>
          </div>
        </div>
      </section>


      <section className="bg-brand-surface py-section" aria-labelledby="process-title">
        <div className="mx-auto max-w-[1180px] px-6">
          <p className="mb-4 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">How we work</p>
          <h2 id="process-title" className="mb-8 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">A clear path from idea to launch</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['01', 'Discuss', 'We talk through your business, audience, goals and what the website needs to achieve.'],
              ['02', 'Design & build', 'We create a tailored website with mobile usability, performance and clear customer journeys in mind.'],
              ['03', 'Launch & support', 'We test, launch and discuss the ongoing support and improvements that suit your business.'],
            ].map(([number, title, description]) => (
              <article key={number} className="rounded-xl2 border border-brand-border bg-white p-7 shadow-card">
                <span className="mb-4 block text-sm font-bold tracking-[0.14em] text-brand-primary">{number}</span>
                <h3 className="mb-3 text-xl font-semibold text-brand-heading">{title}</h3>
                <p className="leading-[1.7] text-brand-textSoft">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices exclude="website-development" title="Pair your website with" tone="muted" />

      <section className="bg-gradient-to-br from-[#1a56c0] to-[#2b7bd8] py-section text-white" aria-labelledby="next-step-title">
        <div className="mx-auto max-w-[860px] px-6 text-center">
          <h2 id="next-step-title" className="mb-5 text-[clamp(2rem,3.4vw,3.3rem)] font-bold leading-[1.1] text-white">
            Ready for a website that works harder for your business?
          </h2>
          <p className="mx-auto mb-9 max-w-[640px] text-[1.05rem] leading-[1.75] text-blue-50">
            Whether you're starting from scratch, replacing an outdated website or exploring something
            more advanced, let's discuss what would work best for your business.
          </p>
          <Link to={WEBSITE_QUOTE} className={`${LIGHT_BUTTON} w-full sm:w-auto`}>
            Request a Quote
          </Link>
          <p className="mt-6 text-[0.95rem] text-blue-50">
            Not sure what you need yet?{" "}
            <Link to={WEBSITE_ENQUIRY} className="font-semibold text-white underline underline-offset-4 hover:text-blue-50">
              Start with a website review
            </Link>
          </p>
        </div>
      </section>

    </div>
  )
}
